<template>
  <!-- The signed-out page frame (LogIn, Register boards; C7 on wide).
       Under 700 px: the iPhone board, a back chevron, the large title and
       the form, with the foot line pinned to the bottom. From 700 px: the
       app's desktop Welcome, the brand over a fixed 400 px card; with a
       `tab` the card leads with the Log in / Create account control instead
       of the title. One DOM for both, so a form never remounts on resize. -->
  <div class="af" :class="{ 'af--tabs': !!tab }">
    <!-- Wide: the app's Welcome art behind the window (the fractal, the
         rings, the fade). -->
    <welcome-fractal v-if="wide" class="af-art">
      <welcome-rings class="af-rings" />
    </welcome-fractal>
    <div class="af-top" aria-hidden="true" />
    <div class="af-nav">
      <button
        v-if="back"
        type="button"
        class="af-back"
        aria-label="Back"
        @click="goBack"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <path d="M15 5l-7 7 7 7" />
        </svg>
      </button>
      <router-link v-else to="/" class="af-home" aria-label="cloudloop home">
        <cl-mark :size="38" color="currentColor" aria-hidden="true" />
      </router-link>
    </div>

    <router-link to="/" class="af-brand" aria-label="cloudloop home">
      <span class="af-brand-mark"
        ><cl-mark :size="57.18" color="currentColor" aria-hidden="true"
      /></span>
      <span class="af-brand-name">cloudloop</span>
      <span class="af-brand-tag">Make music together.</span>
    </router-link>

    <section class="af-card">
      <cl-segmented
        v-if="tab"
        class="af-seg"
        :model-value="tab"
        :options="tabs"
        label="Log in or create an account"
        @update:model-value="switchTab"
      />
      <header v-if="title || $slots.art" class="af-head">
        <span v-if="mark" class="af-markbox">
          <cl-mark :size="44.47" color="currentColor" aria-hidden="true" />
        </span>
        <slot name="art" />
        <h1 v-if="title" class="af-title">{{ title }}</h1>
        <p v-if="subtitle || $slots.subtitle" class="af-sub">
          <slot name="subtitle">{{ subtitle }}</slot>
        </p>
      </header>
      <slot />
      <div class="af-grow" />
      <p v-if="$slots.foot" class="af-foot"><slot name="foot" /></p>
      <p v-if="tab === 'login'" class="af-legal">
        By continuing you agree to the
        <router-link to="/terms">Terms</router-link> and
        <router-link to="/privacy">Privacy Policy</router-link>.
      </p>
    </section>
    <!-- Wide only: what cloudloop is, under the card (Welcome fills it). -->
    <div v-if="$slots.intro" class="af-intro"><slot name="intro" /></div>
  </div>
</template>

<script>
import ClMark from "@/components/kit/ClMark.vue";
import ClSegmented from "@/components/kit/ClSegmented.vue";
import WelcomeRings from "@/components/auth/WelcomeRings.vue";
import WelcomeFractal from "@/components/auth/WelcomeFractal.vue";
import { authLink } from "@/kit/auth.js";

// Where the phone page hands over to the C7 window (plan section 2).
const WIDE = "(min-width: 700px)";

const PATHS = { login: "/login", signup: "/sign-up" };

export default {
  name: "AuthFrame",
  components: { ClMark, ClSegmented, WelcomeFractal, WelcomeRings },
  props: {
    // "login" | "signup": the wide card shows the segmented control.
    tab: { type: String, default: null },
    title: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    // The brand mark above the title (LogIn board).
    mark: { type: Boolean, default: false },
    // A back chevron on phones; otherwise the mark links home.
    back: { type: Boolean, default: true },
    // Where back goes when there is no history to return to.
    backTo: { type: String, default: "/" },
  },
  data: () => ({
    tabs: [
      { value: "login", label: "Log in" },
      { value: "signup", label: "Create account" },
    ],
    wide: typeof window !== "undefined" && !!window.matchMedia && window.matchMedia(WIDE).matches,
  }),
  mounted() {
    this.media = window.matchMedia ? window.matchMedia(WIDE) : null;
    this.onMedia = (e) => (this.wide = e.matches);
    this.media?.addEventListener?.("change", this.onMedia);
  },
  beforeUnmount() {
    this.media?.removeEventListener?.("change", this.onMedia);
  },
  methods: {
    goBack() {
      const state = window.history.state;
      if (state && state.back) this.$router.back();
      else this.$router.push(this.backTo);
    },
    switchTab(tab) {
      if (tab === this.tab || !PATHS[tab]) return;
      const to = authLink(PATHS[tab], this.$route);
      // Between the two forms the URL just follows the tab.
      const onForm = Object.values(PATHS).includes(this.$route.path);
      if (onForm) this.$router.replace(to);
      else this.$router.push(to);
    },
  },
};
</script>

<style scoped>
.af {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--cl-bg);
  color: var(--cl-label);
}

/* Wide-only pieces */
.af-rings,
.af-brand,
.af-seg,
.af-legal,
.af-intro {
  display: none;
}

/* ---- Phone (the iPhone board, without its status bar: the space it took
   comes back only on a board-tall window) ---- */
.af-top {
  flex: none;
  height: clamp(0px, calc(100dvh - 790px), 54px);
}
.af-nav {
  flex: none;
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 4px;
}
.af-back,
.af-home {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--cl-r-md);
  background: transparent;
  color: var(--cl-accent-text);
  cursor: pointer;
}
.af-home {
  width: auto;
  padding: 0 12px;
  color: var(--cl-accent);
}
.af-back:hover {
  background: var(--cl-hover);
}
.af-back:focus-visible,
.af-home:focus-visible {
  outline: 2px solid var(--cl-accent);
  outline-offset: -2px;
}
.af-card {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}
/* 28 between the subtitle and the group on the board, whose 17 px line
   box is 2 shorter than the ramp's 22. */
.af-head {
  padding: 12px 20px 0;
  margin-bottom: 26px;
}
.af-markbox {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 28px;
  /* 16 on the board, plus the 4 its inline mark adds under the baseline */
  margin-bottom: 20px;
  color: var(--cl-accent);
}
.af-title {
  margin: 0;
  font: var(--cl-large-title);
  letter-spacing: var(--cl-large-title-tracking);
  color: var(--cl-label);
}
.af-sub {
  margin: 4px 0 0;
  font: var(--cl-body);
  color: var(--cl-label-2);
}
.af-grow {
  flex: 1 0 24px;
}
.af-foot {
  margin: 0;
  padding: 0 20px 44px;
  text-align: center;
  font: var(--cl-subhead);
  color: var(--cl-label-2);
}
.af-foot :deep(a),
.af-foot :deep(button) {
  font: inherit;
  font-weight: 600;
  color: var(--cl-accent-text);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

/* ---- Wide: the C7 card ---- */
@media (min-width: 700px) {
  .af {
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
  }
  .af-top,
  .af-nav,
  .af-grow {
    display: none;
  }
  .af-rings {
    display: block;
  }
  .af-brand {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 28px;
    color: var(--cl-label);
  }
  .af-brand-mark {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 36px;
    color: var(--cl-accent);
  }
  .af-brand-name {
    margin-top: 10px;
    font: 700 32px/1.1 var(--cl-font-display);
    letter-spacing: -1px;
    color: var(--cl-label);
  }
  .af-brand-tag {
    margin-top: 8px;
    font: 400 17px/22px var(--cl-font);
    color: var(--cl-label-78);
  }
  .af-card {
    position: relative;
    flex: none;
    width: 400px;
    max-width: 100%;
    padding: 16px 0 20px;
    background: var(--cl-window);
    border: 1px solid var(--border-subtle);
    border-radius: var(--cl-r-2xl);
    box-shadow: 0 0 40px var(--cl-scrim);
    box-sizing: border-box;
  }
  .af-seg {
    display: flex;
    margin: 0 16px 20px;
  }
  /* The segmented control names the form; the title stays for screen
     readers only. */
  .af--tabs .af-head {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: 0;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
  .af--tabs .af-foot {
    display: none;
  }
  .af-head {
    padding: 4px 20px 0;
    margin-bottom: 20px;
  }
  .af-markbox {
    display: none;
  }
  .af-title {
    font: var(--cl-title2);
    letter-spacing: 0;
  }
  .af-foot {
    padding: 20px 20px 0;
  }
  /* The app's _LegalLine: 20 under the button, a 1.45 caption line, 40
     to the card's edge (20 here plus the card's own 20). */
  .af-legal {
    display: block;
    margin: 0;
    padding: 20px 24px 20px;
    text-align: center;
    font: var(--cl-caption);
    line-height: 1.45;
    color: var(--cl-label-3);
  }
  .af-legal a {
    text-decoration: underline;
  }
  .af-intro {
    position: relative;
    display: block;
    width: 640px;
    max-width: 100%;
    margin-top: 28px;
  }
}
/* A short window keeps the card whole rather than scroll past the intro. */
@media (min-width: 700px) and (max-height: 779px) {
  .af-intro {
    display: none;
  }
}
</style>

<style>
/* Shared by the forms slotted into AuthFrame (their own scoped styles
   can't reach each other). */
.auth-form {
  display: flex;
  flex-direction: column;
}
.auth-cta.cl-btn {
  align-self: stretch;
  margin: 24px 16px 0 !important;
}
.auth-aside {
  display: flex;
  justify-content: flex-end;
  padding: 10px 20px 0;
  font: var(--cl-subhead);
}
.auth-aside a {
  color: var(--cl-accent-text);
}
</style>
